import Dropdown from '@components/common/formControl/dropdown/dropdown';
import { memo, ReactElement, useMemo } from 'react';
import { IProductFieldsProps } from '.';
import { useAvailableProductsListQuery } from '@services/hooks/site-leads/useAvailableProductsListQuery';
import { useGetRxDb } from '@hooks/getRxdbData';
import { AvailableProductsList } from '@services/hooks/site-leads';
import { AvailableProducts } from '@components/siteLeads/components/siteAddress';
import { useUserRoleType } from '@hooks/useUserRoleType';

interface ProductDropdownProps {
	id: string;
	name: string;
	label: string;
	title: string;
	error: string | null;
	options: AvailableProducts[];
	onBlur: (e: React.FocusEvent) => void;
	setFieldValue: (field: string, value: string) => void;
	disabled: boolean;
	required?: boolean;
	meetingType?: string;
}

interface ProductFieldConfig {
	id: string;
	name: string;
	label: string;
	required?: boolean;
	disabled?: boolean;
}

const ProductDropdown = ({
	id,
	name,
	label,
	title,
	error,
	options,
	onBlur,
	setFieldValue,
	disabled,
	required,
}: ProductDropdownProps): ReactElement => (
	<li className="d-col d-col-2">
		<Dropdown
			id={id}
			name={name}
			label={label}
			title={title}
			error={error}
			options={options}
			onBlur={onBlur}
			setFieldValue={setFieldValue}
			disabled={disabled}
			required={required}
		/>
	</li>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getFieldError = (formik: any, fieldName: string): string | null => {
	return formik.touched[fieldName] && formik.errors[fieldName] ? formik.errors[fieldName] : null;
};

const renderProductField = (
	fieldConfig: ProductFieldConfig,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	formik: any,
	availableProductsList: AvailableProducts[],
	isEditing: boolean,
	showConditionally: boolean = false
): ReactElement | null => {
	const { id, name, label, required, disabled } = fieldConfig;
	const shouldShow = showConditionally ? formik.values[name] : true;

	if (!shouldShow) return null;

	return (
		<ProductDropdown
			key={id}
			id={id}
			name={name}
			label={label}
			title={formik.values[name]}
			error={getFieldError(formik, name)}
			options={availableProductsList}
			onBlur={formik.handleBlur}
			setFieldValue={formik.setFieldValue}
			disabled={disabled ? disabled : isEditing}
			required={required}
		/>
	);
};

export const AddProductsFields = (props: IProductFieldsProps): ReactElement => {
	const {
		formik,
		primaryFocusProduct,
		secondaryFocusProduct,
		product1Discussed,
		product2Discussed,
		isEditing,
		meetingType,
	} = props;
	const roleType = useUserRoleType();
	const isRBDM = roleType === 'RBDM';

	useAvailableProductsListQuery();

	const { result: availableProductsDataList } = useGetRxDb<AvailableProductsList>('products');
	const availableProductsList = useMemo((): AvailableProducts[] => {
		if (availableProductsDataList?.length) {
			return availableProductsDataList.map((product, index) => ({
				id: index,
				value: product?.productgroup3description_c,
			}));
		}
		return [];
	}, [availableProductsDataList]);

	const productFieldsConfig: ProductFieldConfig[] = [
		{
			id: primaryFocusProduct,
			name: primaryFocusProduct,
			label: 'Primary Focus Products',
			required: true,
			disabled: meetingType === 'MDP',
		},
		{ id: secondaryFocusProduct, name: secondaryFocusProduct, label: 'Secondary Focus Products' },
		{ id: product1Discussed, name: product1Discussed, label: 'Product 1 Discussed', required: true },
		{ id: product2Discussed, name: product2Discussed, label: 'Product 2 Discussed', required: true },
	];

	const renderFields = (showConditionally: boolean): (ReactElement | null)[] => {
		return productFieldsConfig.map((fieldConfig) =>
			renderProductField(fieldConfig, formik, availableProductsList, isEditing, showConditionally)
		);
	};

	return <>{renderFields(isRBDM)}</>;
};
export default memo(AddProductsFields);
